import { toastr } from 'react-redux-toastr';
import { BASE_PRINT_AGENT_URL } from '../../config/endpoints';
import { PIN_PAD_OPERATION } from '../../config/constants';
import { createPdfDocument } from '../pdf';
import {
  printSaleVoucher,
  pageWidth,
  pageHeight,
  pageHeightQr,
} from './TotalPosSale';
import { printVoidVoucher } from './TotalPosVoid';

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// jsPDF's 'p' orientation forces height >= width and silently swaps the two
// otherwise — the QR voucher is shorter than it is wide, so it needs 'l'
// (see TotalPosSale.js)
const getPageFormat = (isQrPayment) => ({
  orientation: isQrPayment ? 'l' : 'p',
  size: [pageWidth, isQrPayment ? pageHeightQr : pageHeight],
});

// reprints the vouchers of already persisted pinpad transactions (from the
// reprint endpoint) as one single print job, one copy per transaction — a
// reprint only replaces a lost voucher, so unlike the post-sale print
// (addSaleVoucherCopies) it doesn't duplicate customer/merchant copies.
// `fallbackTab` must be opened by the caller synchronously inside the click,
// before any await, or the browser blocks it as a popup
const printPinPadVoucherReprint = async ({
  transactions,
  operation,
  fallbackTab,
}) => {
  const printVoucher =
    operation === PIN_PAD_OPERATION.VOID ? printVoidVoucher : printSaleVoucher;

  // one pinpad operation = one voucher: skip repeated rows of the same
  // financial reference so a duplicated record never prints twice
  const printedReferences = new Set();
  const uniqueTransactions = transactions.filter(({ financialReference }) => {
    if (!financialReference) return true;
    if (printedReferences.has(financialReference)) return false;
    printedReferences.add(financialReference);
    return true;
  });

  let doc = null;
  uniqueTransactions.forEach((transaction) => {
    const isQrPayment = transaction.paymentMethod === 'QR';
    const { orientation, size } = getPageFormat(isQrPayment);
    if (doc) doc.addPage(orientation, 'mm', size);
    else doc = createPdfDocument(orientation, 'mm', size);
    printVoucher(doc, transaction, isQrPayment);
  });

  doc.autoPrint();

  const arrayBuffer = doc.output('arraybuffer');
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  const pdfBase64 = arrayBufferToBase64(arrayBuffer);

  try {
    await fetch(`${BASE_PRINT_AGENT_URL}/health`);
  } catch {
    // eslint-disable-next-line
    if (fallbackTab) fallbackTab.location.href = blobUrl;
    toastr.error(
      'Error',
      'El agente de impresión no está corriendo. Abre print-agent.exe primero.',
    );
    return;
  }

  try {
    const response = await fetch(`${BASE_PRINT_AGENT_URL}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pdfBase64, printingType: 'voucher' }),
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Error al imprimir');
    }

    if (fallbackTab) fallbackTab.close();
  } catch (err) {
    // eslint-disable-next-line
    if (fallbackTab) fallbackTab.location.href = blobUrl;
    toastr.error('Error', err.message);
  }
};

export default printPinPadVoucherReprint;
